import React, { useState } from 'react';
import { reviewForDeclass } from '../services/api';
import SCGRuleLink from '../components/SCGRuleLink';

const CLASSIFICATION_LEVELS = ['UNCLASSIFIED', 'CONFIDENTIAL', 'SECRET', 'TOP SECRET'];

export default function DeclassReview() {
  const [text, setText] = useState('');
  const [originalClassification, setOriginalClassification] = useState('SECRET');
  const [originalDeclassDate, setOriginalDeclassDate] = useState('');
  const [originalExemption, setOriginalExemption] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await reviewForDeclass(
        text,
        originalClassification,
        originalDeclassDate || null,
        originalExemption || null
      );
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Review request failed');
    } finally {
      setLoading(false);
    }
  };

  const recommendationStyle = (rec) => {
    if (!rec) return {};
    const r = rec.toLowerCase();
    if (r === 'declassify') return { backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' };
    if (r === 'downgrade') return { backgroundColor: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.3)' };
    // maintain
    return { backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' };
  };

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
          Declassification Review
        </h1>
        <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginTop: '6px' }}>
          Evaluate documents for potential declassification per EO 13526
        </p>
      </div>

      {/* Input Section */}
      <div className="vault-card" style={{ padding: '20px', marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Document Text
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste the classified document text here..."
          rows={12}
          style={{
            width: '100%',
            backgroundColor: 'var(--vault-bg)',
            border: '1px solid var(--vault-border)',
            borderRadius: '8px',
            padding: '12px',
            color: 'var(--text-primary)',
            fontSize: '14px',
            fontFamily: 'monospace',
            resize: 'vertical',
            outline: 'none',
          }}
        />

        {/* Classification fields row */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '16px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Original Classification
            </label>
            <select
              value={originalClassification}
              onChange={(e) => setOriginalClassification(e.target.value)}
              style={{
                width: '100%',
                backgroundColor: 'var(--vault-bg)',
                border: '1px solid var(--vault-border)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              {CLASSIFICATION_LEVELS.map((level) => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Classification Date
            </label>
            <input
              type="date"
              value={originalDeclassDate}
              onChange={(e) => setOriginalDeclassDate(e.target.value)}
              style={{
                width: '100%',
                backgroundColor: 'var(--vault-bg)',
                border: '1px solid var(--vault-border)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ flex: '1 1 200px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Exemption Code
            </label>
            <input
              type="text"
              value={originalExemption}
              onChange={(e) => setOriginalExemption(e.target.value)}
              placeholder="e.g. 25X1"
              style={{
                width: '100%',
                backgroundColor: 'var(--vault-bg)',
                border: '1px solid var(--vault-border)',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                outline: 'none',
              }}
            />
          </div>
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !text.trim()}
          style={{
            marginTop: '20px',
            padding: '10px 24px',
            borderRadius: '8px',
            border: 'none',
            fontWeight: 600,
            fontSize: '14px',
            cursor: loading || !text.trim() ? 'not-allowed' : 'pointer',
            backgroundColor: loading || !text.trim() ? 'var(--vault-elevated)' : 'var(--spotlight-gold)',
            color: loading || !text.trim() ? 'var(--text-muted)' : '#000',
            opacity: loading || !text.trim() ? 0.6 : 1,
            transition: 'all 0.2s',
          }}
        >
          {loading ? 'Reviewing...' : 'Review for Declassification'}
        </button>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div className="vault-card" style={{ padding: '24px', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', width: '24px', height: '24px', border: '3px solid var(--vault-border)', borderTopColor: 'var(--spotlight-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p style={{ color: 'var(--text-muted)', marginTop: '12px', fontSize: '14px' }}>
            Analyzing document for declassification eligibility...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="vault-card" style={{ padding: '16px', borderLeft: '3px solid #ef4444' }}>
          <p style={{ color: '#ef4444', fontSize: '14px', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="vault-card" style={{ padding: '24px' }}>
          {/* Recommendation badge + years */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <span style={{
              display: 'inline-block',
              padding: '6px 16px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              ...recommendationStyle(result.recommendation),
            }}>
              {result.recommendation}
            </span>
            {result.years_since_classification != null && (
              <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                {result.years_since_classification} year{result.years_since_classification !== 1 ? 's' : ''} since original classification
              </span>
            )}
          </div>

          {/* Reasoning */}
          {result.reasoning && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Reasoning
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                {result.reasoning}
              </p>
            </div>
          )}

          {/* Current classification assessment */}
          {result.current_classification && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Current Classification Assessment
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                {result.current_classification}
              </p>
            </div>
          )}

          {/* Exemption analysis */}
          {result.exemption_analysis && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Exemption Analysis
              </h3>
              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>
                {result.exemption_analysis}
              </p>
            </div>
          )}

          {/* Applicable rules */}
          {result.applicable_rules && result.applicable_rules.length > 0 && (
            <div>
              <h3 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>
                Applicable SCG Rules ({result.applicable_rules.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {result.applicable_rules.map((rule, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '10px 14px',
                      backgroundColor: 'var(--vault-bg)',
                      borderRadius: '6px',
                      border: '1px solid var(--vault-border)',
                    }}
                  >
                    <SCGRuleLink ruleId={rule.rule_id} className="text-sm font-mono" />
                    {rule.classification_level && (
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: 'var(--vault-elevated)',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                        {rule.classification_level}
                      </span>
                    )}
                    {rule.item && (
                      <span style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                        {rule.item}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
